import { useState, useEffect, useRef } from "react";
import { Message } from "./Message";
import { ChatInput } from "./ChatInput";
import { KitchenInventory } from "./KitchenInventory";
import LoadingState from './ui/LoadingState';

interface ChatMessage {
  content: string;
  isBot: boolean;
}

export interface TranscriptMessage {
  role: 'agent' | 'user';
  content: string;
}

const INITIAL_GREETING = {
  content: "Hi! I'm Kitty, your kitchen assistant! 😺 Here is what I can do:\n\n• Recipe suggestions based on what you have in your stock list\n• Shopping list updates to keep track of what you need\n• Kitchen inventory management to know what you have on hand\n• Keep on track of your saved recipes",
  isBot: true,
};

export const Chat = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_GREETING]);
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);  // Start as false
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (message: string) => {
    setMessages((prev) => [
      ...prev,
      { content: message, isBot: false }
    ]);
  };

  const handleConversationStart = () => {
    setIsInitializing(true);
    setMessages([]); // Clear previous messages when starting new conversation
  };

  const handleTranscriptUpdate = (transcript: TranscriptMessage[]) => {
    const newMessages = transcript.map((msg) => ({
      content: msg.content,
      isBot: msg.role === 'agent'
    }));
    setIsInitializing(false);
    setMessages(newMessages);
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-background relative">
      <KitchenInventory 
        open={isInventoryOpen} 
        onOpenChange={setIsInventoryOpen} 
      />
      
      <div className="flex-1 overflow-y-auto px-4 pb-24 messages-container">
        <div className="max-w-2xl mt-4 mx-auto pt-20">
          {isInitializing ? (
            <LoadingState />
          ) : (
            messages.map((message, index) => (
              <Message
                key={index}
                content={message.content}
                isBot={message.isBot}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-background">
        <div className="w-full max-w-2xl mx-auto px-4 pb-safe">
          <ChatInput 
            onSendMessage={handleSendMessage} 
            onTranscriptUpdate={handleTranscriptUpdate}
            onConversationStart={handleConversationStart}
          />
        </div>
      </div>
    </div>
  );
};